import { Injectable } from '@angular/core';
import { ServiceBase } from 'app/shared/serviceBase';
import { AuthenticationService } from 'app/shared/services/authentication-service';
import { HttpClient } from '@angular/common/http';
import { CompanyListModel } from '../models/company-list.model';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CompanyAddModel } from '../models/company-add-model';
import { CompanyDetailsModel } from '../models/company-details-model';
import { CompanyAuditListModel } from '../models/company-audit-list.model';
import { CompanyAuditAddModel } from '../models/company-audit-add.model';
import { CompanyAuditDetailsModel } from '../models/company-audit-details.model';
import { CompanyUserListModel } from '../models/company-user-list-model';
import { CompanyUserModel } from '../models/company-user-model';
import { CompanyAuditTypeListModel } from '../models/company-audit-type-list.model';
import { CompanyTypeModel } from '../models/company-type-model';
import { AggregatorModel } from '../models/aggregator-model';
import { CompanyAuditTemplateDetailsModel } from '../models/company-audit-template-details.model';
import { CompanyLenderPermissionModel } from '../models/company-lender-permission.model';
import { AuditTargetTimeframeCategoryModel } from '../models/audit-target-timeframe-category.model';
import { AuditTargetModel } from '../models/audit-target.model';

@Injectable()
export class CompanyService extends ServiceBase {
    private commonUrl: string = this.url + '/Company/';
    private accountUrl: string = this.url + '/Account/';
    constructor(
        private _http: HttpClient,
        private _authentication: AuthenticationService
    ) {
        super();
    }

    create(model: CompanyAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    update(model: CompanyDetailsModel): Observable<Response> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    deleteCompany(id: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + id;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    getAll(): Observable<CompanyListModel[]> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .get<CompanyListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    get(id: string): Observable<CompanyDetailsModel> {
        const completeUrl: string = this.commonUrl + id;
        return this._http
            .get<CompanyDetailsModel>(completeUrl).pipe(
                catchError(this.handleError));
    }

    createCompanyAudit(model: CompanyAuditAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'company-audit';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    getCompanyAudits(id: string): Observable<CompanyAuditListModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audits/' + id;
        return this._http
            .get<CompanyAuditListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCompanyAudit(id: string): Observable<CompanyAuditDetailsModel> {
        const completeUrl: string = this.commonUrl + 'company-audit/' + id;
        return this._http
            .get<CompanyAuditDetailsModel>(completeUrl).pipe(
                catchError(this.handleError));
    }

    updateCompanyAudit(model: CompanyAuditDetailsModel): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'company-audit/';
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    deleteCompanyAudit(id: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'company-audit/' + id;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    getUsers(companyId: string): Observable<CompanyUserListModel[]> {
        const completeUrl: string = this.commonUrl + 'user/' + companyId;
        return this._http.get<CompanyUserListModel[]>(completeUrl).pipe(
            catchError(this.handleError));
    }

    addUser(newUser: CompanyUserModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'user';
        return this._http.post<string>(completeUrl, newUser).pipe(
            catchError(this.handleError));
    }

    deleteUser(id: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'user/' + id;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    createCompanyAuditType(model: CompanyAuditAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'audit-type';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    getCompanyAuditTypes(companyId: string): Observable<CompanyAuditTypeListModel[]> {
        const completeUrl: string = this.commonUrl + 'audit-types/' + companyId;
        return this._http
            .get<CompanyAuditTypeListModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    deleteCompanyAuditType(companyId: string, auditTypeId: number): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'audit-type/' + companyId + '/' + auditTypeId;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    getCompanyTypes(): Observable<CompanyTypeModel[]> {
        const completeUrl: string = this.commonUrl + 'types';
        return this._http
            .get<CompanyTypeModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAggregators(): Observable<AggregatorModel[]> {
        const completeUrl: string = this.commonUrl + 'aggregators';
        return this._http
            .get<AggregatorModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCompanyAuditTemplates(id: string): Observable<CompanyAuditTemplateDetailsModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audit-templates/' + id;
        return this._http
            .get<CompanyAuditTemplateDetailsModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    addAuditTemplateToCompany(model: CompanyAuditTemplateDetailsModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'add-audit-template';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    deleteAuditTemplate(companyId: string, auditTemplateId: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'delete-audit-template/' + companyId + '/' + auditTemplateId;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    getCompanyLenderPermissions(id: string): Observable<CompanyLenderPermissionModel[]> {
        const completeUrl: string = this.commonUrl + 'lender-permissions/' + id;
        return this._http
            .get<CompanyLenderPermissionModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    setLenderAccess(model: CompanyLenderPermissionModel): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'set-lender-permission';
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    getAuditTimeframeCategories(): Observable<AuditTargetTimeframeCategoryModel[]> {
        const completeUrl: string = this.commonUrl + 'audits-target-timeframe-categories';
        return this._http
            .get<AuditTargetTimeframeCategoryModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCompanyAuditTargets(id: string): Observable<AuditTargetModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audit-targets/' + id;
        return this._http
            .get<AuditTargetModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    updateCompanyAuditTarget(model: AuditTargetModel): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'update-company-audit-target';
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }


    getAllCompaniesForUser(): Observable<CompanyDetailsModel[]> {
        const completeUrl: string = this.accountUrl + 'getAllCompaniesForUser';
        return this._http
            .get<CompanyDetailsModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }
}
